import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { CreditCard, Loader2, AlertTriangle, RefreshCw, ShieldCheck, Users, ArrowRight, MessageCircle, Mail } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { money, BRAND, whatsappHref } from '@/data/constants';
import { track } from '@/lib/tracking';
import HostedLeadCheckout from '@/components/HostedLeadCheckout';
import VerifyContactPanel from '@/components/VerifyContactPanel';
import { authLink, leadReturnPath } from '@/lib/purchaseIntent';

import {
  SINGLE_LEAD_PRICE,
  STRIPE_ACCOUNT_ID,
  STRIPE_PUBLISHABLE_KEY,
  createLeadIntent,
  routeLead,
  deviceLabel,
  rememberIntent,
  settleLeadPayment,
  leadPaymentStatus,
  isConvertedToCredit,
  isVerificationGate,
  isRiskBlock,
  reportLeadFailure,
  LeadSettlement,
  VerificationGateCode,
} from '@/lib/singleLead';

/**
 * ───────────────────────────────────────────────────────────────────────────
 * $29 SINGLE LEAD — inline card payment (Stripe Elements).
 * ───────────────────────────────────────────────────────────────────────────
 *
 * MOBILE PAY BUTTON — what was wrong and what this component guarantees.
 *
 * The old lead flow had no pay button at all, and the PLAN flow called
 * `window.open()` AFTER an await. Mobile Safari and Chrome on Android only
 * allow a new window inside the synchronous part of a user gesture, so the
 * call was silently swallowed: the tap "did nothing". Nothing here opens a
 * window at all — the card form is inline and `redirect: 'if_required'` keeps
 * the customer on the page.
 *
 * Every failure mode the QA list called out is handled explicitly:
 *   • one submit at a time, guarded by a ref (set synchronously) so the FIRST
 *     tap always registers and the 2nd..nth are ignored — a state-only guard
 *     can lose the race on a fast double tap
 *   • the button is a real <button type="submit"> inside a <form>, 48px tall,
 *     with `touch-action: manipulation` so iOS does not wait for a double-tap
 *     zoom gesture before firing
 *   • an aria-live status line shows "Processing payment…", then a clear
 *     approved / declined / retry message. The customer is NEVER left on the
 *     same screen with no response
 *   • a 40s watchdog turns a hung request into a retryable error instead of a
 *     spinner that never resolves
 *   • errors scroll themselves into view (mobile keyboards cover the bottom
 *     half of the viewport, which is how the original decline message ended up
 *     off-screen)
 *   • settlement is idempotent server-side, so an interrupted network, a
 *     reload, or a retry can never double charge or double credit
 */

const stripePromise =
  STRIPE_ACCOUNT_ID && STRIPE_PUBLISHABLE_KEY
    ? loadStripe(STRIPE_PUBLISHABLE_KEY, { stripeAccount: STRIPE_ACCOUNT_ID })
    : null;

type Phase = 'idle' | 'processing' | 'settling' | 'done' | 'error';

interface FormProps {
  listingId: string;
  slug: string;
  paymentIntentId: string;
  onSettled: (s: LeadSettlement) => void;
}

const PayForm: React.FC<FormProps> = ({ listingId, slug, paymentIntentId, onSettled }) => {
  const { t, lang, path } = useI18n();
  const stripe = useStripe();
  const elements = useElements();
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState('');
  const submitting = useRef(false);
  const [converted, setConverted] = useState<LeadSettlement | null>(null);
  const errorRef = useRef<HTMLDivElement | null>(null);

  // Bring any decline / validation message into view on small screens.
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  const fail = (msg: string) => {
    submitting.current = false;
    setPhase('error');
    setError(msg);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Ref guard is set synchronously — the first tap always wins, repeats are
    // dropped. No duplicate charge is possible from repeated taps.
    if (submitting.current) return;
    if (!stripe || !elements) {
      fail(t('pay.formNotReady'));
      return;
    }
    submitting.current = true;
    setError('');
    setPhase('processing');
    track('cta_click', { action: 'pay_single_lead', device: deviceLabel(), listing: listingId });

    // Watchdog: never leave the customer staring at a spinner.
    const watchdog = window.setTimeout(() => {
      if (submitting.current) fail(t('pay.timeout'));
    }, 40000);

    try {
      rememberIntent({ listingId, slug, paymentIntentId });

      const { error: payError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      });

      window.clearTimeout(watchdog);

      if (payError) {
        // Card declined / incorrect details / expired card / validation.
        // Card-level failures are reported so the SERVER can apply velocity / card-testing lockouts.
        let lockedMsg = '';
        if (payError.type === 'card_error' && paymentIntentId) {
          const r = await reportLeadFailure(paymentIntentId, payError.code, (payError as { decline_code?: string }).decline_code, lang);
          if (r.locked) lockedMsg = r.message || '';
        }
        fail(lockedMsg || payError.message || t('pay.declined'));
        return;
      }
      if (!paymentIntent || paymentIntent.status !== 'succeeded') {
        fail(t('pay.notCompleted'));
        return;
      }

      // Paid. The server marks the order "provider succeeded" before settling and retries transient
      // failures itself; if we still get no answer (503 / timeout / dropped response) we poll the order
      // status — the background reconciler finishes it from the payment id. Never ask the customer to re-pay.
      setPhase('settling');
      let settled = await settleLeadPayment(paymentIntent.id, listingId, lang);
      // Verification gate on settle (normally unreachable — intent is gated first). The money is
      // recorded on the order; it is applied by the same resume path once the buyer verifies.
      if (!settled.ok && isVerificationGate(settled.error)) {
        submitting.current = false;
        setPhase('error');
        setError(lang === 'es'
          ? 'Tu pago se recibió y está guardado. Verifica tu correo electrónico y vuelve a esta página: el lead se desbloqueará automáticamente. No vuelvas a pagar.'
          : 'Your payment was received and is saved. Verify your email address and come back to this page — the lead will unlock automatically. Please do not pay again.');
        return;
      }
      for (let i = 0; !settled.ok && i < 8; i++) {
        await new Promise((r) => setTimeout(r, i < 3 ? 1500 : 4000));
        const st = await leadPaymentStatus(paymentIntent.id);
        if (st.ok) { settled = st; break; }
        if (i === 2) settled = await settleLeadPayment(paymentIntent.id, listingId, lang);
      }

      submitting.current = false;
      if (!settled.ok) {
        // Money moved but the unlock is still being applied in the background. Honest, non-alarming state.
        setPhase('error');
        setError(lang === 'es'
          ? 'Tu pago se recibió y se está aplicando automáticamente. En unos minutos verás el lead desbloqueado o 1 crédito de lead en tu saldo. No vuelvas a pagar.'
          : 'Your payment was received and is being applied automatically. Within a few minutes you will see the lead unlocked or 1 lead credit in your balance. Please do not pay again.');
        return;
      }
      setPhase('done');
      if (isConvertedToCredit(settled)) setConverted(settled);
      onSettled(settled);
    } catch (err) {
      window.clearTimeout(watchdog);
      fail((err as Error)?.message || t('pay.networkError'));
    }
  };

  if (converted) {
    return (
      <div role="status" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-900">
        <p className="font-extrabold">
          {lang === 'es' ? 'Este lead se agotó — tu pago ahora es 1 crédito de lead' : 'This lead sold out — your payment is now 1 lead credit'}
        </p>
        <p className="mt-1.5 leading-relaxed">
          {lang === 'es'
            ? 'El proyecto alcanzó su límite de 3 contratistas mientras se procesaba tu pago. No se cobró nada extra: tus $29 se convirtieron en 1 crédito que ya está en tu saldo, en tu página de membresía, en el historial de créditos y en tu historial de pagos.'
            : 'The project reached its limit of 3 contractors while your payment was processing. Nothing extra was charged: your $29 was converted into 1 credit that is already in your balance, on your membership page, in your credit ledger and in your payment history.'}
        </p>
        <p className="mt-1 text-xs font-semibold">
          {lang === 'es' ? `Saldo actual: ${converted.balance} crédito(s)` : `Current balance: ${converted.balance} credit(s)`}
        </p>
        <Link
          to={path('/jobs')}
          className="mt-3 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-3 text-sm font-bold text-white hover:bg-orange-600"
        >
          {lang === 'es' ? 'Elegir otro lead' : 'Choose another lead'} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    );
  }

  const busy = phase === 'processing' || phase === 'settling';

  return (
    <form onSubmit={handleSubmit} noValidate>
      <PaymentElement options={{ layout: 'tabs' }} />

      {error && (
        <div
          ref={errorRef}
          role="alert"
          className="mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          <p className="flex items-start gap-2 font-bold">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
          </p>
          <p className="mt-1.5 text-xs leading-relaxed text-red-700">{t('pay.noChargeOnDecline')}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || busy}
        style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
        className="mt-4 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-3.5 text-base font-bold text-white transition-colors hover:bg-orange-600 focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-300 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <CreditCard className="h-5 w-5" />}
        {/* The settling phase mints the credit AND spends it on THIS listing in
            one server-side transaction, so the label says what is actually
            happening: the selected lead is being unlocked. */}
        {phase === 'processing'
          ? t('pay.processingPayment')
          : phase === 'settling'
          ? t('pay.unlockingLead')
          : `${t('pay.payNow')} — ${money(SINGLE_LEAD_PRICE)}`}
      </button>

      {/* Immediate, screen-reader-announced feedback for every tap. */}
      <p aria-live="polite" className="mt-2 min-h-[18px] text-center text-xs font-semibold text-slate-500">
        {phase === 'processing'
          ? t('pay.processingPayment')
          : phase === 'settling'
          ? t('pay.unlockingLead')
          : phase === 'error'
          ? t('pay.tryAgain')
          : ''}
      </p>



      {phase === 'error' && (
        <button
          type="submit"
          className="mt-1 flex w-full items-center justify-center gap-1.5 rounded-xl border-2 border-slate-300 px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50"
        >
          <RefreshCw className="h-4 w-4" /> {t('pay.retry')}
        </button>
      )}

      <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-400">
        <ShieldCheck className="h-3.5 w-3.5" /> {t('pay.securedBy')}
      </p>
    </form>
  );
};

interface Props {
  listingId: string;
  slug: string;
  /** Called after a successful settlement (unlocked or credit-only). */
  onSettled: (s: LeadSettlement) => void;
  /** Kept for API compatibility — memberships are offered separately by the modal. */
  plansHref?: string;
  onLeavePage?: () => void;
  /**
   * The server refused the purchase because the account is not verified
   * (email_verification_required / phone_verification_required). The page
   * shows its "Send verification link" panel in response.
   */
  onVerificationRequired?: (code: VerificationGateCode) => void;
}

type ViewState = 'loading' | 'ready' | 'hosted' | 'contact' | 'sold_out' | 'owned' | 'verify' | 'signin' | 'error';

/**
 * ───────────────────────────────────────────────────────────────────────────
 * ROUTE FIRST, THEN PAY  (2026-09-29 root-cause fix)
 * ───────────────────────────────────────────────────────────────────────────
 * Before: the card form was chosen because a Stripe account id was hard-coded.
 * That account has charges disabled, so EVERY buyer hit "No valid payment
 * method types" and was pointed at memberships — a dead end for a $29 buyer.
 *
 * Now the server decides (single-lead-purchase `route`):
 *   inline  -> card form (only when the connected account can actually charge)
 *   hosted  -> automatic redirect to the working hosted $29 checkout
 *   contact -> nothing can take money; admins were alerted, buyer gets WhatsApp/email
 * And if the card form's PaymentIntent fails anyway, the same response carries a
 * hosted checkout URL, so the buyer is redirected instead of stranded.
 */
const SingleLeadPayment: React.FC<Props> = ({ listingId, slug, onSettled, onLeavePage, onVerificationRequired }) => {
  const { t, lang, path } = useI18n();
  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [hostedUrl, setHostedUrl] = useState('');
  const [state, setState] = useState<ViewState>('loading');
  const [message, setMessage] = useState('');
  const [attempt, setAttempt] = useState(0);
  /** Which verification gate the server returned (email vs phone). */
  const [gateCode, setGateCode] = useState('');
  const es = lang === 'es';

  useEffect(() => {
    let active = true;
    setState('loading');
    setMessage('');
    (async () => {
      // 1. Ask the server which checkout can actually take money right now.
      const route = await routeLead(listingId, lang);
      if (!active) return;
      if (route.alreadyOwned) return setState('owned');
      if (route.soldOut) return setState('sold_out');
      if (isVerificationGate(route.error)) {
        setGateCode(route.error);
        setState('verify');
        setMessage(route.message || '');
        if (route.error !== 'phone_verification_required') onVerificationRequired?.(route.error);
        return;
      }
      if (isRiskBlock(route.error)) {
        setMessage(route.message || '');
        return setState('error');
      }
      if (route.error === 'not_authenticated') return setState('signin');
      if (route.mode === 'hosted' && route.hostedUrl) {
        track('cta_click', { action: 'single_lead_routed_hosted', reason: route.reason.slice(0, 60), listing: listingId });
        setHostedUrl(route.hostedUrl);
        return setState('hosted');
      }
      if (route.mode === 'contact') return setState('contact');

      // 2. Inline is healthy (or routing was unreachable) — create the PaymentIntent.
      if (!stripePromise) return setState('contact');
      const res = await createLeadIntent(listingId, lang);
      if (!active) return;
      if (res.alreadyOwned) return setState('owned');
      if (res.soldOut) return setState('sold_out');
      if (isVerificationGate(res.error)) {
        setGateCode(res.error);
        setState('verify');
        setMessage(res.message || '');
        // Phone is verified inline in this form; only the email gate hands off to the page.
        if (res.error !== 'phone_verification_required') onVerificationRequired?.(res.error);
        return;
      }
      if (isRiskBlock(res.error)) {
        setMessage(res.message || '');
        return setState('error');
      }
      if (res.error === 'not_authenticated') return setState('signin');
      // 3. The card path failed — the server already issued a working hosted checkout.
      if (res.hostedUrl) {
        track('cta_click', { action: 'single_lead_intent_fallback_hosted', listing: listingId });
        setHostedUrl(res.hostedUrl);
        return setState('hosted');
      }
      if (!res.clientSecret || !res.paymentIntentId) {
        setMessage(res.message && !/gateway_|payment method/i.test(res.message) ? res.message : t('pay.intentFailed'));
        return setState('error');
      }
      setClientSecret(res.clientSecret);
      setPaymentIntentId(res.paymentIntentId);
      setState('ready');
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId, lang, attempt]);

  const options = useMemo(
    () => ({ clientSecret, appearance: { theme: 'stripe' as const } }),
    [clientSecret]
  );

  /** Human help — always present on any non-payable state, so there is never a dead end. */
  const contactLinks = (
    <div className="mt-3 flex flex-col gap-2">
      <a
        href={whatsappHref(lang)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => track('cta_click', { action: 'whatsapp', channel: 'single-lead-blocked', listing: listingId })}
        className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] px-4 py-3 text-sm font-bold text-white hover:bg-[#1ebe5b]"
      >
        <MessageCircle className="h-4 w-4" /> {es ? 'Comprar por WhatsApp' : 'Buy via WhatsApp'}
      </a>
      <a
        href={`mailto:${BRAND.email}?subject=${encodeURIComponent(`$29 lead — ${slug || listingId}`)}`}
        className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border-2 border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50"
      >
        <Mail className="h-4 w-4" /> {BRAND.email}
      </a>
    </div>
  );

  if (state === 'loading') {
    return (
      <p className="flex items-center justify-center gap-2 rounded-xl bg-slate-50 px-4 py-4 text-sm font-semibold text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> {es ? 'Preparando pago seguro…' : 'Preparing secure checkout…'}
      </p>
    );
  }

  if (state === 'hosted' && hostedUrl) {
    return <HostedLeadCheckout url={hostedUrl} listingId={listingId} slug={slug} />;
  }

  if (state === 'contact') {
    return (
      <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-900">
        <p className="font-bold">
          {es
            ? 'El pago en línea no está disponible en este momento. Nuestro equipo ya fue notificado.'
            : 'Online payment is unavailable right now. Our team has already been notified.'}
        </p>
        <p className="mt-1 text-xs">
          {es ? 'Escríbenos y te enviamos un enlace de pago en minutos. No se ha cobrado nada.' : 'Message us and we will send a payment link within minutes. Nothing has been charged.'}
        </p>
        {contactLinks}
      </div>
    );
  }

  if (state === 'signin') {
    // Session expired mid-purchase: sign-in must bring the buyer BACK to this
    // lead with checkout re-opening (?buy=1), in the right language. The old
    // link used a raw `/${lang}/login` (not the Spanish route) and dropped the
    // purchase, so the buyer landed on the dashboard after signing in.
    const back = leadReturnPath(window.location.pathname);
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm font-semibold text-amber-900">
        {es ? 'Tu sesión expiró. Inicia sesión de nuevo para comprar este lead.' : 'Your session expired. Please sign in again to buy this lead.'}
        <Link
          to={authLink(path('/login'), back, 'lead')}
          onClick={onLeavePage}
          className="mt-3 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm font-bold text-white hover:bg-slate-800"
        >
          {es ? 'Iniciar sesión y volver a este lead' : 'Sign in and come back to this lead'} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    );
  }

  if (state === 'sold_out') {
    return (
      <div className="rounded-xl border border-slate-300 bg-slate-50 px-4 py-4 text-sm text-slate-700">
        <p className="flex items-center gap-2 font-extrabold uppercase tracking-wide">
          <Users className="h-4 w-4" /> {t('leads.soldOutTitle')}
        </p>
        <p className="mt-2 leading-relaxed">{t('pay.soldOutNoCharge')}</p>
      </div>
    );
  }

  if (state === 'owned') {
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm font-semibold text-emerald-800">
        {t('pay.alreadyOwned')}
      </div>
    );
  }

  if (state === 'verify') {
    const needsPhone = gateCode === 'phone_verification_required';
    return (
      <div role="alert" className="rounded-xl border-2 border-amber-300 bg-amber-50 px-4 py-4 text-sm text-amber-900">
        <p className="flex items-start gap-2 font-bold">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          {message || (es ? 'Verifique su correo electrónico antes de comprar un lead.' : 'Verify your email address before buying a lead.')}
        </p>
        <p className="mt-1.5 text-xs leading-relaxed text-amber-800">{es ? 'No se ha cobrado nada.' : 'Nothing has been charged.'}</p>
        {needsPhone ? (
          /* Phone-before-first-purchase (fraud control): verify right here, then continue. */
          <div className="mt-3 text-slate-900">
            <VerifyContactPanel />
            <button
              type="button"
              onClick={() => setAttempt((a) => a + 1)}
              style={{ touchAction: 'manipulation' }}
              className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-xl bg-amber-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-amber-700"
            >
              <RefreshCw className="h-4 w-4" /> {es ? 'Ya verifiqué — continuar al pago' : 'I verified — continue to payment'}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onLeavePage?.()}
            style={{ touchAction: 'manipulation' }}
            className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center rounded-xl bg-amber-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-amber-700"
          >
            {es ? 'Verificar ahora' : 'Verify now'}
          </button>
        )}
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-800">
        <p className="font-bold">{message || t('pay.intentFailed')}</p>
        <p className="mt-1 text-xs">{es ? 'No se ha cobrado nada.' : 'Nothing has been charged.'}</p>
        <button
          type="button"
          onClick={() => setAttempt((a) => a + 1)}
          style={{ touchAction: 'manipulation' }}
          className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-xl bg-red-700 px-4 py-2 text-sm font-bold text-white hover:bg-red-800"
        >
          <RefreshCw className="h-4 w-4" /> {t('pay.retry')}
        </button>
        {contactLinks}
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      <PayForm listingId={listingId} slug={slug} paymentIntentId={paymentIntentId} onSettled={onSettled} />
    </Elements>
  );
};

export default SingleLeadPayment;
